import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, User, Phone, Brain, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { extractCandidateName } from "@/utils/screeningNameExtraction";
import { calculateEnhancedScreeningScore, getRecommendationFromScore } from "@/utils/enhancedScreeningScore";

interface ScreeningSession {
  id: string;
  candidate_id: string;
  session_status: string;
  transcript: string;
  screening_results: any;
  duration_seconds: number;
  created_at: string;
  completed_at?: string;
  is_inbound_call: boolean;
  candidate_lookup_query?: string;
  ai_summary?: any;
  candidates?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    candidate_number: string;
  };
}

interface ScreeningCardProps {
  session: ScreeningSession;
}

export const ScreeningCard = ({ session }: ScreeningCardProps) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Use the enhanced name extraction utility
  const candidateName = extractCandidateName(session);

  // Extract insights using enhanced scoring
  const extractInsights = () => {
    // Use enhanced scoring algorithm
    const scoreData = calculateEnhancedScreeningScore({
      transcript: session.transcript,
      ai_summary: session.ai_summary,
      screening_results: session.screening_results,
      duration_seconds: session.duration_seconds,
      session_status: session.session_status
    });

    // Extract strengths and concerns from AI summary or screening results
    const aiSummary = session.ai_summary;
    const screeningResults = session.screening_results;
    
    let keyStrengths = [];
    let keyConcerns = [];
    
    if (aiSummary?.screening_assessment?.green_flags) {
      keyStrengths = aiSummary.screening_assessment.green_flags.slice(0, 2);
    } else if (screeningResults?.strengths) {
      keyStrengths = screeningResults.strengths.slice(0, 2);
    }
    
    if (aiSummary?.screening_assessment?.red_flags) {
      keyConcerns = aiSummary.screening_assessment.red_flags.slice(0, 2);
    } else if (screeningResults?.concerns) {
      keyConcerns = screeningResults.concerns.slice(0, 2);
    }
    
    // Add penalty information to concerns if score is low
    if (scoreData.penalties.length > 0 && scoreData.totalScore < 50) {
      keyConcerns = [...keyConcerns, ...scoreData.penalties.slice(0, 1)].slice(0, 2);
    }
    
    return {
      score: scoreData.totalScore,
      recommendation: getRecommendationFromScore(scoreData.totalScore),
      keyStrengths,
      keyConcerns,
      scoreBreakdown: scoreData.breakdown,
      penalties: scoreData.penalties,
      qualityGates: scoreData.qualityGates
    };
  };

  const insights = extractInsights();

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4 flex-1">
            <div className="flex items-center gap-2">
              {getStatusIcon(session.session_status)}
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="font-medium text-lg">{candidateName}</div>
                {session.candidates?.candidate_number && (
                  <Badge variant="outline" className="text-xs">
                    {session.candidates.candidate_number}
                  </Badge>
                )}
              </div>
              
              {session.candidates?.email && (
                <div className="text-sm text-muted-foreground mb-3">
                  {session.candidates.email}
                </div>
              )}

              {/* Key Insights */}
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">Score:</span>
                    <span className={`text-sm font-bold ${getScoreColor(insights.score)}`}>
                      {insights.score}/100
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    <span className="text-sm">
                      <span className="font-medium">Recommendation:</span> {insights.recommendation}
                    </span>
                  </div>
                </div>
                
                {insights.keyStrengths.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium text-green-600">Strengths:</span>{' '}
                    {insights.keyStrengths.join(', ')}
                  </div>
                )}
                
                {insights.keyConcerns.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium text-red-600">Concerns:</span>{' '}
                    {insights.keyConcerns.join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-3">
              <Badge className={getStatusColor(session.session_status)}>
                {session.session_status}
              </Badge>
              
              <div className="text-right text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(session.created_at))} ago
                </div>
                {session.duration_seconds && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {Math.round(session.duration_seconds / 60)}m duration
                  </div>
                )}
              </div>
              
              <Button variant="outline" size="sm" asChild>
                <Link to={`/screening/${session.id}`}>
                  View Details
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};